import { connect, ConnectedProps } from 'react-redux';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SARConfiguration } from 'dashboard/models/sar-configuration';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setSARConfiguration } from 'dashboard/redux/slices/sar-configuration';
import { RootState } from 'dashboard/redux/store';
import { loadSARConfiguration } from 'dashboard/redux/thunk-actions/sar-configuration';
import DashboardSARConfigurationShowView from 'dashboard/views/DashboardSARConfigurationShowView/DashboardSARConfigurationShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardSARConfigurationShowRouteDispatchProps = (props: PropsFromRedux) => {
  const { loading: loadingSARConfiguration } = useApiLoader({
    loader: () => props.loadSARConfiguration(props.organization.id),
    deps: [props.loadSARConfiguration, props.organization.id],
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        props.showMessage('Failed to load SAR configuration.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleCreateSARConfiguration, error: handleCreateSARConfigurationError } =
    useApiCallback(
      async (sarConfigurationData: Record<string, any>) => {
        const sarConfigurationResponse =
          await SessionClientDashboard.postResourceCreate<SARConfiguration>({
            attributes: sarConfigurationData,
            id: props.organization.id,
            type: ApiModel.SARConfiguration,
          });

        props.setSARConfiguration(sarConfigurationResponse.data);
        props.showMessage('Successfully updated SAR configuration.', MessageType.Success, true);
      },
      [props.sarConfiguration, props.setSARConfiguration],
    );

  useApiErrorHandler({
    error: handleCreateSARConfigurationError,
    onError: () =>
      props.showMessage(
        'Failed to create SAR configuration, please try again.',
        MessageType.Failure,
      ),
    onNetworkError: () =>
      props.showMessage(
        'Failed to create SAR configuration, please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleUpdateSARConfiguration, error: handleUpdateSARConfigurationError } =
    useApiCallback(
      async (sarConfigurationData: Record<string, any>) => {
        const sarConfigurationResponse =
          await SessionClientDashboard.updateResource<SARConfiguration>({
            attributes: sarConfigurationData,
            id: props.organization.id,
            type: ApiModel.SARConfiguration,
          });

        props.setSARConfiguration(sarConfigurationResponse.data);
        props.showMessage('Successfully updated SAR configuration.', MessageType.Success, true);
      },
      [props.sarConfiguration, props.setSARConfiguration],
    );

  useApiErrorHandler({
    error: handleUpdateSARConfigurationError,
    onError: () =>
      props.showMessage(
        'Failed to update SAR configuration, please try again.',
        MessageType.Failure,
      ),
    onNetworkError: () =>
      props.showMessage(
        'Failed to update SAR configuration, please try again.',
        MessageType.Failure,
      ),
  });

  if (loadingSARConfiguration) {
    return (
      <Backdrop>
        <LoadingIndicator />
      </Backdrop>
    );
  }

  return (
    <DashboardSARConfigurationShowView
      sarConfiguration={props.sarConfiguration}
      updateSARConfiguration={handleUpdateSARConfiguration}
      createSARConfiguration={handleCreateSARConfiguration}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    sarConfiguration: state.sarConfiguration.sarConfiguration,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  showMessage,
  setSARConfiguration,
  loadSARConfiguration,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardSARConfigurationShowRouteDispatchProps);
